import { useEffect, useRef, useState } from "react";
import {
  BookOpen,
  ChevronDown,
  ClipboardList,
  Cloud,
  FileText,
  FileType2,
  Layers,
  Loader2,
  PackageOpen,
  Projector,
  RefreshCw,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, driveStatus, saveActivityToDrive } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const sanitize = (name) =>
  (name || "activity").replace(/[^A-Za-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "") || "activity";

const filenameFromHeader = (header, fallback) => {
  if (!header) return fallback;
  const m = /filename="?([^"]+)"?/i.exec(header);
  return (m && m[1]) || fallback;
};

const suffixForKey = (key) => {
  if (key === "slides") return "_StudentDeck";
  if (key === "script" || key === "script_docx") return "_TeacherScript";
  if (key === "script_supply") return "_SupplyPlan";
  if (key === "reading" || key === "reading_docx") return "_StudentReading";
  if (key === "reading_trio") return "_StudentReading_AllModes";
  if (key === "worksheet") return "_Worksheet";
  if (key === "package") return "_Package";
  return "";
};

// Shared dropdown menu styles. `left-0` anchors the menu to the
// trigger's left edge. `max-w-[calc(100vw-1.5rem)]` is the mobile
// safety net — the menu never extends past the viewport's right edge,
// which is the failure mode we saw in production on small phones.
const menuClass =
  "absolute left-0 top-full mt-2 z-30 min-w-[12rem] max-w-[calc(100vw-1.5rem)] rounded-md border border-[#4a6984]/15 bg-white shadow-lg overflow-hidden";

// Top-level so React doesn't see a fresh component type on each render
// of the parent. Each piece (Lesson Overview, Lesson Script, Student
// Deck, Worksheet, Student Reading, Full Package) renders one of these.
const PieceMenu = ({
  pieceKey,
  label,
  icon: Icon,
  variantClass,
  busy,
  busyKeys,
  isOpen,
  onToggle,
  downloads,
  driveReady,
  driveSaves,
  extraTrailing,
  onDownload,
  onSaveToDrive,
  testid,
}) => {
  const isBusy = busyKeys.some((k) => busy === k);
  return (
    <div className="inline-flex items-center gap-1 relative" data-testid={`${testid}-wrapper`}>
      <button
        type="button"
        onClick={onToggle}
        disabled={!!busy}
        data-testid={testid}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={variantClass}
      >
        {isBusy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Icon className="h-3.5 w-3.5" />
        )}
        {label}
        <ChevronDown className="h-3 w-3 ml-0.5" />
      </button>
      {extraTrailing}
      {isOpen && !busy && (
        <div role="menu" data-testid={`${testid}-menu`} className={menuClass}>
          {downloads.map((d, idx) => (
            <button
              key={`dl-${d.key}-${idx}`}
              type="button"
              role="menuitem"
              data-testid={d.testid || `${testid}-download-${d.key}`}
              onClick={() => onDownload(d)}
              className="w-full text-left px-3.5 py-2 text-sm font-medium text-[#4a6984] hover:bg-[#f4f5f7] focus:bg-[#f4f5f7] focus:outline-none inline-flex items-center gap-2"
            >
              {d.icon ? <d.icon className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
              Download {d.short || d.label}
            </button>
          ))}
          {driveReady && driveSaves && driveSaves.length > 0 && (
            <>
              <div className="px-3.5 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/55 border-t border-[#4a6984]/10 bg-[#f4f5f7]/40">
                Save to Google Drive
              </div>
              {driveSaves.map((s) => (
                <button
                  key={`dr-${s.fmt}`}
                  type="button"
                  role="menuitem"
                  data-testid={`${testid}-drive-${s.fmt}`}
                  onClick={() => onSaveToDrive(s, label)}
                  className="w-full text-left px-3.5 py-2 text-sm font-medium text-[#1c2d3d] hover:bg-[#96ded1]/15 focus:bg-[#96ded1]/15 focus:outline-none inline-flex items-center gap-2"
                >
                  <Cloud className="h-3.5 w-3.5 text-[#1c2d3d]/70" />
                  {s.label}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const ExportButtons = ({
  activityId,
  activityName,
  hasScript = false,
  hasHandout = false,
  // Phase 88 — Math Engine lessons keep their own gate flag so the
  // worksheet button appears for them without flipping the existing
  // `hasHandout` semantics (which controls Generator-lesson printables).
  hasMathHandout = false,
  // Jun 2026 — Evidence Sheet v2. The generator-lesson Worksheet slot now
  // renders the transferable-skill Evidence Sheet, so its button appears when
  // the lesson has a resolvable skill sheet (not on handout presence).
  hasSkillEvidence = false,
  // Feb 2026 — Reading Generator output. When true the activity has
  // `reading_markdown` populated and we expose a Reading dropdown
  // (PDF / Word + Save-to-Drive variants).
  hasReading = false,
  // Phase 114.F.1 — Reading text type. Used to gate the "All 3 modes"
  // trio entry: only the 7 prose text types support Support+Enrichment
  // companions; Comic Strip and Primary Source are Anchor-only by spec.
  readingTextType = null,
  // Jun 2026 — the set of reading levels generated for this activity
  // (subset of {"anchor","support","enrichment"}). When more than one
  // level exists, the Reading dropdown lists a separate download per
  // level. Empty / single-level activities keep the single "Student
  // Reading" download (backward compatible).
  readingModes = [],
  // Feb 2026 — show a Full Package piece dropdown (Download + Save to
  // Drive). Lesson Workspace renders a primary package CTA separately,
  // so this defaults off; callers using ExportButtons in isolation
  // (e.g., Library card) can pass `showPackage` to expose it.
  showPackage = false,
  size = "md",
  onlyKeys,
  // Jun 2026 — Unit artifacts are multi-lesson containers, not a single
  // teachable lesson. When true: hide the Student Slide Deck (a unit has
  // no single deck) and relabel the Overview piece "Unit Overview"
  // (its PDF/Word download already renders the unit doc — see
  // routes/activities.py `_render_activity`, output_type=="unit" → build_pdf).
  isUnit = false,
  // Jun 2026 — a summative_task is an assessment, not a lesson. Relabel the
  // Overview piece "Task Overview" (the PDF/Word download already renders the
  // culminating task doc via exports._pick_markdown → summative_markdown).
  isSummative = false,
}) => {
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  // Drive status — null while loading. We only show Save-to-Drive
  // entries inside each piece's dropdown when both gates pass.
  const [drive, setDrive] = useState(null);
  const [openMenu, setOpenMenu] = useState(null); // 'overview' | 'script' | 'reading' | 'slides' | 'worksheet' | 'package' | null
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!user) {
      setDrive(null);
      return;
    }
    let cancelled = false;
    driveStatus()
      .then((s) => {
        if (!cancelled) setDrive(s);
      })
      .catch(() => {
        if (!cancelled) setDrive({ configured: false, connected: false });
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Close menu on any outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  if (!activityId) return null;
  const pad = size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm";

  // ----- Download / Save-to-Drive helpers -----
  const download = async ({ key, label, ext, timeout, extraParams }, { regenerate = false } = {}) => {
    if (busy) return;
    const busyKey = regenerate ? `${key}_regen` : key;
    setBusy(busyKey);
    setOpenMenu(null);
    const isSlides = key === "slides";
    const toastId = toast.loading(
      regenerate
        ? "Redesigning student slides…"
        : isSlides
        ? "Designing student slides…"
        : `Generating ${label}…`,
      isSlides
        ? {
            description: regenerate
              ? "Throwing out the old draft and starting fresh — about 30 sec."
              : "Drafting kid-facing slides paced with the lesson — about 30 sec.",
          }
        : undefined,
    );
    try {
      const params = { format: key };
      if (regenerate) params.regenerate = true;
      if (extraParams) Object.assign(params, extraParams);
      const res = await api.get(`/activities/${activityId}/export`, {
        params,
        responseType: "blob",
        timeout: timeout || 60000,
      });
      const fallback = `${sanitize(activityName)}${suffixForKey(key)}.${ext}`;
      const fname = filenameFromHeader(res.headers["content-disposition"], fallback);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success(regenerate ? "New deck downloaded" : `${label} downloaded`, {
        id: toastId,
        description: fname,
      });
    } catch (err) {
      // Phase 115.2 Fix 2 — when responseType=blob, FastAPI's JSON
      // error payload arrives as a Blob; axios doesn't auto-parse it.
      // Try to read the blob as JSON so the server's `detail` (e.g.,
      // the "What would you like to call this activity?" prompt for
      // untitled worksheets) lands in the toast instead of a generic
      // "request failed" message.
      let detail = err?.response?.data?.detail || err?.message;
      if (
        !detail &&
        err?.response?.data instanceof Blob &&
        err.response.data.type?.includes("application/json")
      ) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          detail = parsed?.detail || text;
        } catch {
          /* leave detail undefined */
        }
      } else if (
        err?.response?.data instanceof Blob &&
        !(err.response.data.type || "").startsWith("application/pdf")
      ) {
        // Other text-ish blob (HTML error page etc.) — read raw.
        try {
          detail = (await err.response.data.text()) || detail;
        } catch {
          /* noop */
        }
      }
      const finalDetail = detail || "The server didn't respond in time.";
      toast.error(`${label} download failed`, { id: toastId, description: String(finalDetail) });
    } finally {
      setBusy(null);
    }
  };

  const saveToDrive = async (fmt, label) => {
    if (busy) return;
    setBusy(`drive_${fmt}`);
    setOpenMenu(null);
    const toastId = toast.loading(`Saving ${label} to your Google Drive…`);
    try {
      const file = await saveActivityToDrive(activityId, fmt);
      toast.success("Saved to your Atomic Edu Lessons folder", {
        id: toastId,
        description: file?.name,
        action: file?.view_url
          ? { label: "Open in Drive", onClick: () => window.open(file.view_url, "_blank", "noreferrer") }
          : undefined,
        duration: 8000,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Drive upload failed";
      toast.error(`Couldn't save ${label} to Drive`, { id: toastId, description: String(detail) });
    } finally {
      setBusy(null);
    }
  };

  const driveReady = !!(drive?.configured && drive?.connected);

  // Button styling helpers — passed into the top-level PieceMenu via
  // `variantClass` so the trigger can match the row's existing look.
  const baseBtn =
    "inline-flex items-center gap-2 rounded-md border font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed";
  const plain = `${baseBtn} border-[#4a6984]/20 bg-white text-[#4a6984] hover:bg-[#f4f5f7] hover:border-[#4a6984]/35 ${pad}`;
  const accent = `${baseBtn} border-[#96ded1] bg-[#96ded1]/25 text-[#1c2d3d] hover:bg-[#96ded1]/45 hover:border-[#1c2d3d]/40 ${pad}`;

  // Pre-bound handlers passed down to each PieceMenu.
  const handleDownload = (d) =>
    download({ key: d.key, label: d.label, ext: d.ext, timeout: d.timeout, extraParams: d.extraParams });
  const handleSaveToDrive = (s, pieceLabel) =>
    saveToDrive(s.fmt, `${pieceLabel} — ${s.short || s.label}`);
  const makeToggle = (pieceKey) => () =>
    setOpenMenu((m) => (m === pieceKey ? null : pieceKey));

  // `onlyKeys` lets specific lessons (supply plans, math activations, etc)
  // restrict the pieces that render.
  const onlySet = onlyKeys && onlyKeys.length ? new Set(onlyKeys) : null;
  const showOverview = !onlySet || onlySet.has("pdf") || onlySet.has("docx");
  // Units never expose a Student Slide Deck (no single-lesson deck exists).
  const showStudentDeck = !isUnit && (!onlySet || onlySet.has("slides"));
  const overviewLabel = isUnit
    ? "Unit Overview"
    : isSummative
    ? "Task Overview"
    : "Lesson Overview";
  const showScript = hasScript && (!onlySet || onlySet.has("script"));
  // Phase 114.F.1.b — async regenerate flow for the Reading Trio.
  // The cold-cache + force-regenerate paths take 50-90s (2 parallel
  // Claude calls) which exceeds the Kubernetes ingress idle timeout.
  // So instead of a long-blocking GET, we POST → 202 → poll → fetch
  // the now-cached trio PDF in <500 ms.
  const regenerateReadingTrio = async () => {
    if (busy) return;
    setBusy("reading_trio_regen");
    const toastId = toast.loading(
      "Regenerating Support + Enrichment companions… (a minute or two)",
    );
    try {
      const start = await api.post(
        `/activities/${activityId}/reading-companions/regenerate`,
      );
      const startedAt = start.data?.started_at;
      // Poll status every 4s, up to ~3 minutes max so a hung Claude
      // call doesn't spin forever.
      const deadline = Date.now() + 3 * 60 * 1000;
      let ready = false;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          const s = await api.get(
            `/activities/${activityId}/reading-companions/status`,
          );
          const status = s.data || {};
          // "Ready" once support + enrichment cached AND regenerate
          // run finished AND its updated_at is newer than our
          // started_at (so we don't accept a stale pre-cache as the
          // result of *our* regenerate click).
          if (
            status.ready
            && !status.regenerating
            && status.updated_at
            && status.updated_at >= startedAt
          ) {
            ready = true;
            break;
          }
        } catch {
          // Transient — keep polling.
        }
      }
      if (!ready) {
        toast.error("Regeneration is still running in the background. Try 'All 3 modes' in a minute.", {
          id: toastId,
        });
        return;
      }
      toast.success("Companions ready — downloading the trio PDF…", { id: toastId });
      // Now hit the (instant) cached trio export.
      await download({
        key: "reading_trio",
        label: "All 3 reading modes",
        ext: "pdf",
        timeout: 30000,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unknown error";
      toast.error("Couldn't regenerate companions", { id: toastId, description: String(detail) });
    } finally {
      setBusy(null);
    }
  };

  const showReading = hasReading && (!onlySet || onlySet.has("reading"));
  // Phase 114.F.1 — the "All 3 modes" trio is only meaningful for the
  // 7 prose text types. Comic Strip + Primary Source Micro-Read are
  // Anchor-only by spec (their length is fixed by panel/excerpt
  // conventions) so the trio entry is hidden for those.
  const _PROSE_READING_TYPES = new Set([
    "did_you_know",
    "field_notes",
    "short_fiction",
    "news_article",
    "interview",
    "infographic",
    "opinion_piece",
  ]);
  const readingSupportsTrio = showReading && _PROSE_READING_TYPES.has(readingTextType);
  // Jun 2026 — per-level reading downloads. When the activity carries
  // more than one generated level, list a separate PDF + Word download
  // per level (each hits the export with `?mode=`). Single-level
  // activities keep the plain "Student Reading" download.
  const _READING_MODE_ORDER = ["anchor", "support", "enrichment"];
  const _READING_MODE_LABELS = {
    anchor: "Anchor",
    support: "Support",
    enrichment: "Enrichment",
  };
  const _readingLevels = _READING_MODE_ORDER.filter((m) =>
    (readingModes || []).includes(m),
  );
  const _multiLevelReading = _readingLevels.length > 1;
  const _trioEntry = {
    key: "reading_trio",
    label: "All 3 levels (PDF)",
    short: "Anchor + Support + Enrichment",
    ext: "pdf",
    icon: Layers,
    timeout: 120000,
    testid: "export-reading-trio",
  };
  let readingDownloads;
  if (_multiLevelReading) {
    readingDownloads = _readingLevels.flatMap((m) => {
      const lbl = _READING_MODE_LABELS[m];
      return [
        {
          key: "reading",
          label: `Student Reading — ${lbl} (PDF)`,
          short: `${lbl} · PDF`,
          ext: "pdf",
          icon: FileText,
          extraParams: { mode: m },
          testid: `export-reading-${m}`,
        },
        {
          key: "reading_docx",
          label: `Student Reading — ${lbl} (Word)`,
          short: `${lbl} · Word (.docx)`,
          ext: "docx",
          icon: FileType2,
          extraParams: { mode: m },
          testid: `export-reading-${m}-docx`,
        },
      ];
    });
    if (readingSupportsTrio) readingDownloads.push(_trioEntry);
  } else if (readingSupportsTrio) {
    readingDownloads = [
      { key: "reading", label: "Student Reading (PDF)", short: "PDF", ext: "pdf", icon: FileText },
      { key: "reading_docx", label: "Student Reading (Word)", short: "Word (.docx)", ext: "docx", icon: FileType2 },
      _trioEntry,
    ];
  } else {
    readingDownloads = [
      { key: "reading", label: "Student Reading (PDF)", short: "PDF", ext: "pdf", icon: FileText },
      { key: "reading_docx", label: "Student Reading (Word)", short: "Word (.docx)", ext: "docx", icon: FileType2 },
    ];
  }
  const showWorksheet = (hasSkillEvidence || hasMathHandout) && (!onlySet || onlySet.has("worksheet"));
  // Units expose no "Complete Lesson Package" — a package is a
  // single-lesson artifact; a unit is a container. (Founder ruling
  // `emergent_reply_hide_unit_package.md`, Jun 2026.)
  const showPackagePiece = showPackage && !isUnit && (!onlySet || onlySet.has("package"));

  // ============================================================
  // Each piece is a self-contained dropdown with Download + (when
  // Drive is connected) per-piece Save-to-Drive options.
  // ============================================================
  return (
    <div ref={wrapperRef} className="flex flex-wrap items-center gap-2 relative" data-testid="export-buttons">
      {showStudentDeck && (
        <PieceMenu
          pieceKey="slides"
          label="Student Slide Deck"
          icon={Projector}
          variantClass={accent}
          busy={busy}
          isOpen={openMenu === "slides"}
          onToggle={makeToggle("slides")}
          onDownload={handleDownload}
          onSaveToDrive={handleSaveToDrive}
          driveReady={driveReady}
          testid="export-slides"
          busyKeys={["slides", "slides_regen", "drive_gslides", "drive_slides"]}
          downloads={[
            { key: "slides", label: "Student Deck", short: "PowerPoint (.pptx)", ext: "pptx", timeout: 120000, icon: Projector },
          ]}
          driveSaves={[
            { fmt: "gslides", label: "Google Slides", short: "Google Slides" },
            { fmt: "slides", label: "PowerPoint (.pptx)", short: "PowerPoint" },
          ]}
          extraTrailing={
            <button
              type="button"
              onClick={() =>
                download(
                  { key: "slides", label: "Student Deck", ext: "pptx", timeout: 120000 },
                  { regenerate: true },
                )
              }
              disabled={!!busy}
              data-testid="export-slides-regenerate"
              aria-label="Regenerate student deck"
              title="Regenerate student deck"
              className={`inline-flex items-center justify-center rounded-md border border-[#96ded1] bg-white text-[#1c2d3d] hover:bg-[#96ded1]/25 hover:border-[#1c2d3d]/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                size === "sm" ? "h-7 w-7" : "h-9 w-9"
              }`}
            >
              {busy === "slides_regen" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
            </button>
          }
        />
      )}

      {showWorksheet && (
        <PieceMenu
          pieceKey="worksheet"
          label="Transferable Skills Worksheet"
          icon={ClipboardList}
          variantClass={plain}
          busy={busy}
          isOpen={openMenu === "worksheet"}
          onToggle={makeToggle("worksheet")}
          onDownload={handleDownload}
          onSaveToDrive={handleSaveToDrive}
          driveReady={driveReady}
          testid="export-worksheet"
          busyKeys={["worksheet", "drive_worksheet"]}
          downloads={[{ key: "worksheet", label: "Transferable Skills Worksheet", short: "PDF", ext: "pdf", icon: FileText }]}
          driveSaves={[{ fmt: "worksheet", label: "PDF", short: "PDF" }]}
        />
      )}

      {/* Row break — keeps the layout in two tidy rows on wider screens. */}
      <div className="basis-full h-0" aria-hidden />

      {showOverview && (
        <PieceMenu
          pieceKey="overview"
          label={overviewLabel}
          icon={FileText}
          variantClass={plain}
          busy={busy}
          isOpen={openMenu === "overview"}
          onToggle={makeToggle("overview")}
          onDownload={handleDownload}
          onSaveToDrive={handleSaveToDrive}
          driveReady={driveReady}
          testid="export-overview"
          busyKeys={["pdf", "docx", "drive_pdf", "drive_docx", "drive_gdoc"]}
          downloads={[
            { key: "pdf", label: `${overviewLabel} (PDF)`, short: "PDF", ext: "pdf", icon: FileText },
            { key: "docx", label: `${overviewLabel} (Word)`, short: "Word (.docx)", ext: "docx", icon: FileType2 },
          ]}
          driveSaves={[
            { fmt: "gdoc", label: "Google Doc", short: "Google Doc" },
            { fmt: "pdf", label: "PDF", short: "PDF" },
            { fmt: "docx", label: "Word (.docx)", short: "Word" },
          ]}
        />
      )}

      {showScript && (
        <PieceMenu
          pieceKey="script"
          label="Teacher Script"
          icon={FileText}
          variantClass={plain}
          busy={busy}
          isOpen={openMenu === "script"}
          onToggle={makeToggle("script")}
          onDownload={handleDownload}
          onSaveToDrive={handleSaveToDrive}
          driveReady={driveReady}
          testid="export-script"
          busyKeys={["script", "script_docx", "script_supply", "drive_script", "drive_gdoc_script"]}
          downloads={[
            { key: "script", label: "Teacher Script (PDF)", short: "PDF", ext: "pdf", icon: FileText },
            { key: "script_docx", label: "Teacher Script (Word)", short: "Word (.docx)", ext: "docx", icon: FileType2 },
            {
              key: "script_supply",
              label: "Supply Plan (PDF)",
              short: "Supply Plan (substitute-ready)",
              ext: "pdf",
              icon: UserCheck,
              testid: "export-script-supply",
            },
          ]}
          driveSaves={[
            { fmt: "gdoc_script", label: "Google Doc", short: "Google Doc" },
            { fmt: "script", label: "PDF", short: "PDF" },
          ]}
        />
      )}

      {showReading && (
        <PieceMenu
          pieceKey="reading"
          label="Student Reading"
          icon={BookOpen}
          variantClass={accent}
          busy={busy}
          isOpen={openMenu === "reading"}
          onToggle={makeToggle("reading")}
          onDownload={handleDownload}
          onSaveToDrive={handleSaveToDrive}
          driveReady={driveReady}
          testid="export-reading"
          busyKeys={[
            "reading",
            "reading_docx",
            "reading_trio",
            "reading_trio_regen",
            "drive_reading",
            "drive_gdoc_reading",
          ]}
          downloads={readingDownloads}
          driveSaves={[
            { fmt: "gdoc_reading", label: "Google Doc", short: "Google Doc" },
            { fmt: "reading", label: "PDF", short: "PDF" },
          ]}
          extraTrailing={readingSupportsTrio ? (
            <button
              type="button"
              onClick={regenerateReadingTrio}
              disabled={!!busy}
              data-testid="export-reading-trio-regenerate"
              aria-label="Regenerate Support + Enrichment companions"
              title="Regenerate the Support + Enrichment companions from scratch (runs in the background, a minute or two)"
              className={`inline-flex items-center justify-center rounded-md border border-[#96ded1] bg-white text-[#1c2d3d] hover:bg-[#96ded1]/25 hover:border-[#1c2d3d]/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                size === "sm" ? "h-7 w-7" : "h-9 w-9"
              }`}
            >
              {busy === "reading_trio_regen" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
            </button>
          ) : undefined}
        />
      )}

      {showPackagePiece && (
        <PieceMenu
          pieceKey="package"
          label="Download Complete Lesson Package — PDF"
          icon={PackageOpen}
          variantClass={plain}
          busy={busy}
          isOpen={openMenu === "package"}
          onToggle={makeToggle("package")}
          onDownload={handleDownload}
          onSaveToDrive={handleSaveToDrive}
          driveReady={driveReady}
          testid="export-package"
          busyKeys={["package", "drive_package"]}
          downloads={[{ key: "package", label: "Complete Lesson Package — PDF", short: "PDF", ext: "pdf", icon: PackageOpen, timeout: 120000 }]}
          driveSaves={[{ fmt: "package", label: "PDF", short: "PDF" }]}
        />
      )}

      {/* Drive not connected → small hint chip so teachers know to
          connect it once and unlock per-piece Save-to-Drive. */}
      {user && drive && !drive.connected && drive.configured && (
        <a
          href="/dashboard?tab=settings"
          data-testid="connect-drive-hint"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4a6984]/70 hover:text-[#1c2d3d] underline decoration-dotted underline-offset-4 ml-1"
        >
          <Cloud className="h-3 w-3" />
          Connect Google Drive
        </a>
      )}
    </div>
  );
};

export default ExportButtons;
